<script setup>
	import { computed, onBeforeUnmount, onMounted, reactive, watch } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";
	import { trackContactDialogOpened, setContactSource, clearContactSource } from "@/services/analytics";
	import TestimonialCarousel from "@/components/TestimonialCarousel.vue";
	import lucentaryLogoUrl from "@/assets/img/lucentary-banner-blue.svg";

	const promoPage = "/excellence-and-discount-promotion-classic";

	const testimonials = [
		{
			author: "Geoff A",
			quote: "Chad is very talented technically, and he brings a wealth of experience and ability to the projects he's involved in. He also has a lot to offer in terms of project management. He is an effective communicator and has experience with a variety of software development methodologies. Finally, and most importantly, Chad is a phenomenal mentor and teacher.",
		},
		{
			author: "Joshua L",
			quote: "Chad is one of those rare individuals whose vision extends beyond just the project at hand. He sees the big picture, both technically, managerially, and in relationships. He is very talented when it comes to technical matters, and also knows how to train and mentor individuals to help them reach their maximum potential.",
		},
		{
			author: "Raul G",
			quote: "We hired Chad for a quick turnaround (3 weeks!) SaaS project and he delivered, exceptionally. Our team and, more importantly, the customers felt the demo prototype was professionally done and exceeded expectations. Chad was extremely responsive, knowledgeable and great to work with.",
		},
		{
			author: "Raj S",
			quote: "In a matter of weeks Chad assumed responsibility for the majority of the user interface. Throughout the project Chad offered fresh options and new ideas allowing us to leverage the latest technology capabilities. In addition to development, Chad took a leadership role in defining our build process and policies.",
		},
		{
			author: "Josh S",
			quote: "Chad is easily the smartest and most innovative architect and engineer I have worked with. Rarely do you come across someone with his breadth and command of technical knowledge. He fundamentally understands how to build robust systems that provide value to his clients. He is also a great communicator.",
		},
		{
			author: "Larry M",
			quote: "Chad is a remarkable architect and developer. He was able to deliver on the project, despite the technical and management challenges. With constant change in technical direction, he was able to adapt well. I hope to work with Chad again in the future.",
		},
		{
			author: "AD A",
			quote: "We hired Chad to help us migrate a number of PHP-based applications to .NET as part of a migration off an old intranet. Without existing documentation, Chad was able to work with business users and the existing code base to re-write three applications from the ground up in a matter of weeks.",
		},
		{
			author: "DJ W",
			quote: "Chad Greer is one of the top Microsoft Software Solution Consultants in the market. He has great depth in architecting and developing custom solutions using an Agile approach to solve complex business problems. He has a natural ability to apply his past experiences to new challenges.",
		},
		{
			author: "Tor N",
			quote: "Chad is a technically gifted individual, with an eye for detail, and operates well as part of a team in respect of innovative and creative approaches to design, development and implementation. He has an impeccable work ethic and is one of the most innovative software architects I've known.",
		},
		{
			author: "Matt B",
			quote: "Chad was my counterpart in defining and developing our company's software, including the database, data operations, the presentation software and the overarching architecture. He understands how to write well-formed and maintainable software, but he was a shining star because he understood how to craft an enterprise solution.",
		},
		{
			author: "Chad S",
			quote: "Chad is without a doubt one of the most innovative software architects I've known and a very powerful technical leader. He has a talent for inspiring passion in others. He is very intelligent, has an impeccable work ethic and does his homework. I highly recommend Chad Greer for any technical leadership position.",
		},
	];

	const capabilities = [
		{
			label: "Commitment One",
			command: "listen",
			title: "Start with your goals",
			description: "We begin by understanding what success looks like for you, then shape the whole engagement around your priorities. Never a template, never an upsell.",
		},
		{
			label: "Commitment Two",
			command: "craft",
			title: "Build it to a higher standard",
			description: "Clean, tested, documented code from professional engineers with two decades of shipping real systems. No shortcuts, no \"good enough,\" no surprises later.",
		},
		{
			label: "Commitment Three",
			command: "stand-behind",
			title: "Own the outcome",
			description: "We're accountable for the result and we guarantee it. If something we delivered breaks, we make it right. Your wins are the only scoreboard we care about.",
		},
	];

	const qualityChecks = [
		"Professional engineers on every project, never juniors",
		"Clean, tested, documented, maintainable code",
		"Your priorities drive the roadmap, every week",
		"Work owned end-to-end, and guaranteed",
	];

	const offerIncludes = [
		"A professional team led by Chad Greer, never offshored",
		"The same uncompromising standard, start to finish",
		"A plan built around your goals, not a template",
		"50% off your first 6 weeks as a new client",
	];

	const data = reactive({
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
	});

	const requestMoreInfoContent = {
		General: {
			title: "Talk through your situation",
			description: "Tell us a little about what you're working on and we'll get back to you shortly. You'll hear from a senior engineer, not a sales rep.",
		},
		GetStarted: {
			title: "Book your free call",
			description: "Tell us a bit about your project and your goals, and we'll set up a free call with a senior engineer. No sales pitch, no obligation.",
		},
	};

	const activeRequestMoreInfoContent = computed(() => requestMoreInfoContent[data.requestMoreInfo.topic] || requestMoreInfoContent.General);

	function requestMoreInfo(topic) {
		data.requestMoreInfo.topic = topic;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.isSuccess = false;
		data.requestMoreInfo.isProcessing = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.isShowing = true;
	}

	function closeRequestMoreInfo() {
		data.requestMoreInfo.isShowing = false;
	}

	function scrollToSection(sectionId) {
		const section = document.getElementById(sectionId);

		if (section) {
			section.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	async function submitRequestForMoreInfo() {
		data.requestMoreInfo.isProcessing = true;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.contactInfo.phoneNumber = "n/a";
		data.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(data.requestMoreInfo.contactInfo))
			.then(() => {
				data.requestMoreInfo.isSuccess = true;
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					data.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
						validationMessage === "Your E-mail Address is required."
							? "Your E-mail Address or Phone Number is required."
							: validationMessage
					);
				} else {
					console.error(error);
					data.requestMoreInfo.isError = true;
				}
			})
			.finally(() => {
				data.requestMoreInfo.isProcessing = false;
			});
	}

	function handleModalKeydown(event) {
		if (event.key === "Escape") {
			closeRequestMoreInfo();
		}
	}

	watch(() => data.requestMoreInfo.isShowing, (isShowing) => {
		if (isShowing) {
			trackContactDialogOpened({ topic: data.requestMoreInfo.topic });

			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleModalKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleModalKeydown);
		}
	});

	onMounted(() => {
		setContactSource(promoPage);
	});

	onBeforeUnmount(() => {
		clearContactSource();

		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", handleModalKeydown);
	});
</script>

<template>
	<div class="excellence-promo">
		<header class="site-header">
			<div class="site-header-inner">
				<span class="brand">
					<img class="brand-logo" :src="lucentaryLogoUrl" alt="Lucentary" />
				</span>
			</div>
		</header>

		<section class="hero">
			<div class="hero-decoration" aria-hidden="true">
				<span class="blob blob-one"></span>
				<span class="blob blob-two"></span>
			</div>

			<div class="hero-inner">
				<div class="hero-copy">
					<span class="offer-badge">50% off your first 6 weeks</span>
					<h1 class="hero-title">
						Excellence is the baseline.
						<span class="hero-title-accent">Your goals set the spec.</span>
					</h1>
					<p class="hero-lead">
						Lucentary exists to do software properly. Professional engineers, an uncompromising
						standard, and a relentless focus on what you actually need, applied to every
						project, every line, from the first call to the final handoff.
					</p>
					<div class="hero-actions">
						<button type="button" class="btn btn-primary" @click="requestMoreInfo('GetStarted')">Book a Free Call</button>
						<button type="button" class="btn btn-secondary" @click="scrollToSection('standard')">How we work</button>
					</div>
					<ul class="hero-assurances">
						<li>
							<span class="hero-assurance-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							Talk directly with a senior engineer
						</li>
						<li>
							<span class="hero-assurance-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							No sales pitch. Just expert advice.
						</li>
						<li>
							<span class="hero-assurance-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							Free, with zero obligation
						</li>
					</ul>
				</div>

				<aside class="hero-aside">
					<figure class="spotlight-card">
						<div class="spotlight-mark" aria-hidden="true">&ldquo;</div>
						<blockquote class="spotlight-quote">
							Chad is easily the smartest and most innovative engineer I have worked with. He
							builds robust systems that provide real value, and explains the hard parts clearly.
						</blockquote>
						<figcaption class="spotlight-author">
							<span class="spotlight-avatar" aria-hidden="true">J</span>
							<span>
								<span class="spotlight-name">Josh S</span>
								<span class="spotlight-role">Chief Technology Officer</span>
							</span>
						</figcaption>
					</figure>
					<div class="hero-chip hero-chip-one">Excellence-obsessed</div>
					<div class="hero-chip hero-chip-two">Client-first</div>
				</aside>
			</div>
		</section>

		<section id="standard" class="offerings">
			<div class="offerings-inner">
				<header class="block-header">
					<span class="block-eyebrow">How we work</span>
					<h2 class="block-heading">Three commitments behind every line</h2>
				</header>
				<div class="offerings-grid">
					<article
						v-for="(capability, capabilityIndex) in capabilities"
						:key="capability.command"
						class="offering-card"
					>
						<span class="offering-step">{{ String(capabilityIndex + 1).padStart(2, "0") }}</span>
						<span class="offering-label">{{ capability.label }}</span>
						<h3 class="offering-title">{{ capability.title }}</h3>
						<p class="offering-description">{{ capability.description }}</p>
					</article>
				</div>
			</div>
		</section>

		<section class="quality">
			<div class="quality-inner">
				<header class="block-header">
					<span class="block-eyebrow">Our standard</span>
					<h2 class="block-heading">What excellence looks like in practice</h2>
				</header>

				<div class="quality-card">
					<ul class="quality-list">
						<li
							v-for="qualityCheck in qualityChecks"
							:key="qualityCheck"
							class="quality-item"
						>
							<span class="quality-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<span class="quality-text">{{ qualityCheck }}</span>
						</li>
					</ul>
					<p class="quality-summary">Four passed, zero compromises. That's the bar on every engagement.</p>
				</div>
			</div>
		</section>

		<section class="offer">
			<div class="offer-inner">
				<div class="offer-card">
					<div class="offer-copy">
						<span class="block-eyebrow">New-client offer</span>
						<h2 class="offer-heading">Start with 50% off your first 6 weeks</h2>
						<p class="offer-lead">
							Same professional team. Same uncompromising standard. Half the cost for your first
							six weeks. No catch, no obligation. It's simply the easiest way to see what
							excellence feels like on your own project.
						</p>
						<ul class="offer-list">
							<li v-for="offerItem in offerIncludes" :key="offerItem" class="offer-item">
								<span class="offer-check" aria-hidden="true">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
								</span>
								{{ offerItem }}
							</li>
						</ul>
						<button type="button" class="btn btn-primary btn-large" @click="requestMoreInfo('GetStarted')">Claim 50% off your first 6 weeks</button>
						<p class="offer-note">Free call with a senior engineer &middot; Not a sales pitch &middot; Limited new-client spots each month</p>
					</div>

					<aside class="deal-card" aria-hidden="true">
						<span class="deal-eyebrow">Welcome offer</span>
						<div class="deal-headline">
							<span class="deal-percent">50%</span>
							<span class="deal-percent-label">off your<br />first 6 weeks</span>
						</div>
						<p class="deal-applied"><span class="deal-applied-check">✓</span> applied · no obligation</p>
					</aside>
				</div>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="cta-card">
					<div class="cta-decoration" aria-hidden="true"></div>
					<h2 class="cta-title">Let's build it to the standard you need</h2>
					<p class="cta-lead">
						Tell us about your project and your goals. You'll get a straight, expert read on
						how to do it right, and exactly how the 50% offer applies to your first six weeks.
						It's a conversation with a senior engineer. Never a sales call.
					</p>
					<div class="cta-actions">
						<button type="button" class="btn btn-light" @click="requestMoreInfo('GetStarted')">Book a Free Call</button>
						<button type="button" class="btn btn-quiet-light" @click="requestMoreInfo('General')">Talk through your situation</button>
					</div>
					<p class="cta-offer">New clients get <strong>50% off the first 6 weeks.</strong></p>
				</div>
			</div>
		</section>

		<section class="testimonials">
			<div class="testimonials-inner">
				<header class="block-header">
					<span class="block-eyebrow">What clients say</span>
					<h2 class="block-heading">In their own words</h2>
				</header>

				<div class="testimonials-carousel">
					<TestimonialCarousel :items="testimonials">
						<template #default="{ item }">
							<figure class="testimonial-card">
								<div class="testimonial-mark" aria-hidden="true">&ldquo;</div>
								<blockquote class="testimonial-quote">{{ item.quote }}</blockquote>
								<figcaption class="testimonial-author">
									<span class="testimonial-avatar" aria-hidden="true">{{ item.author.charAt(0) }}</span>
									{{ item.author }}
								</figcaption>
							</figure>
						</template>
					</TestimonialCarousel>
				</div>
			</div>
		</section>

		<footer class="site-footer">
			<div class="site-footer-inner">
				<span class="brand">
					<img class="brand-logo brand-logo-footer" :src="lucentaryLogoUrl" alt="Lucentary" />
				</span>
				<p class="footer-copy">Professional software services. &copy; {{ new Date().getFullYear() }} Lucentary.</p>
			</div>
		</footer>

		<transition name="modal-fade">
			<div v-if="data.requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeRequestMoreInfo">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="requestMoreInfoTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeRequestMoreInfo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
					<div class="modal-scroll">
						<template v-if="!data.requestMoreInfo.isSuccess">
							<span class="modal-badge">Free &middot; Talk directly with an engineer &middot; No sales pitch</span>
							<h2 id="requestMoreInfoTitle" class="modal-title">{{ activeRequestMoreInfoContent.title }}</h2>
							<p class="modal-description">{{ activeRequestMoreInfoContent.description }}</p>
							<div class="modal-field">
								<label for="requestMoreInfoName">Name</label>
								<input id="requestMoreInfoName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoEmail">Email or phone number</label>
								<input id="requestMoreInfoEmail" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoText">Tell us about your project and your goals</label>
								<textarea id="requestMoreInfoText" v-model="data.requestMoreInfo.contactInfo.text" rows="4" :disabled="data.requestMoreInfo.isProcessing"></textarea>
							</div>
							<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
								<p class="modal-feedback-title">Please fix the following:</p>
								<ul class="modal-feedback-list">
									<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
								</ul>
							</div>
							<p v-else-if="data.requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your message. Please try again.</p>
							<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitRequestForMoreInfo"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Send message" }}</button>
						</template>
						<div v-else class="modal-success">
							<span class="success-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<h2 class="modal-title">Message received</h2>
							<p class="modal-description">Thanks for reaching out. We'll be in touch with you shortly.</p>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
	.excellence-promo {
		--page: #f4f8fd;
		--card: #ffffff;
		--ink: #182d4d;
		--ink-soft: #44546f;
		--ink-muted: #76859c;
		--blue: #3b6fd4;
		--blue-deep: #2a54a8;
		--blue-soft: #dce8fb;
		--sky: #cfe0fb;
		--sky-light: #e6eefc;
		--border: #e2eaf6;
		--cta: #2a54a8;

		font-family: "Poppins", "Avenir Next", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
		color: var(--ink);
		background: var(--page);
		--carousel-accent: var(--blue);
		--carousel-dot: #c3d3ee;
		overflow-x: hidden;
	}

	.site-header {
		position: sticky;
		top: 0;
		z-index: 900;
		background: rgba(244, 248, 253, 0.85);
		backdrop-filter: blur(12px);
		-webkit-backdrop-filter: blur(12px);
		border-bottom: 1px solid var(--border);
	}

	.site-header-inner {
		max-width: 1180px;
		margin: 0 auto;
		padding: 0.85rem 1.5rem;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	.brand {
		display: inline-flex;
		align-items: center;
		text-decoration: none;
	}

	.brand-logo {
		height: 56px;
		width: auto;
		display: block;
	}

	.brand-logo-footer {
		height: 48px;
	}

	.hero {
		position: relative;
		padding: clamp(3.5rem, 8vw, 6.5rem) 1.5rem clamp(3rem, 6vw, 5rem);
		overflow: hidden;
		isolation: isolate;
	}

	.hero-decoration {
		position: absolute;
		inset: 0;
		z-index: -1;
		pointer-events: none;
	}

	.blob {
		position: absolute;
		border-radius: 50%;
		filter: blur(10px);
		opacity: 0.6;
	}

	.blob-one {
		top: -140px;
		right: -100px;
		width: 460px;
		height: 460px;
		background: radial-gradient(circle at 30% 30%, var(--sky), transparent 70%);
	}

	.blob-two {
		bottom: -160px;
		left: -120px;
		width: 380px;
		height: 380px;
		background: radial-gradient(circle at 60% 40%, var(--sky-light), transparent 70%);
	}

	.hero-inner {
		max-width: 1180px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1.05fr 0.95fr;
		gap: clamp(2rem, 5vw, 4.5rem);
		align-items: center;
	}

	.offer-badge {
		display: inline-block;
		padding: 0.55rem 1.2rem;
		border-radius: 999px;
		background: var(--blue);
		color: #ffffff;
		font-size: 0.98rem;
		font-weight: 600;
		margin-bottom: 1.75rem;
		box-shadow: 0 12px 26px -14px rgba(42, 84, 168, 0.7);
	}

	.hero-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2.5rem, 5vw, 4.3rem);
		line-height: 1.04;
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--ink);
		margin: 0 0 1.6rem;
	}

	.hero-title-accent {
		display: block;
		color: var(--blue-deep);
		font-style: italic;
	}

	.hero-lead {
		font-size: clamp(1.1rem, 1.4vw, 1.32rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 54ch;
		margin: 0 0 2.25rem;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 1rem 2.05rem;
		border-radius: 14px;
		font-family: inherit;
		font-size: 1.08rem;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		border: 2px solid transparent;
		transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease, color 0.25s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(42, 84, 168, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(42, 84, 168, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(59, 111, 212, 0.4);
		outline-offset: 3px;
	}

	.btn-secondary {
		background: var(--card);
		color: var(--ink);
		border-color: var(--border);
	}

	.btn-secondary:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-secondary:focus-visible {
		outline: 3px solid rgba(24, 45, 77, 0.25);
		outline-offset: 3px;
	}

	.btn-light {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.45);
	}

	.btn-light:hover {
		color: var(--blue-deep);
		transform: translateY(-2px);
		background: #f1f6ff;
	}

	.btn-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 3px;
	}

	.btn-quiet-light {
		background: transparent;
		color: #ffffff;
		border-color: transparent;
		text-decoration: underline;
		text-underline-offset: 4px;
		text-decoration-thickness: 2px;
		padding-left: 1rem;
		padding-right: 1rem;
	}

	.btn-quiet-light:hover { color: var(--sky); }

	.btn-quiet-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 3px;
	}

	.btn-large {
		padding: 1.1rem 2.2rem;
		font-size: 1.12rem;
	}

	.btn-block {
		width: 100%;
	}

	.hero-assurances {
		list-style: none;
		margin: 1.75rem 0 0;
		padding: 0;
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem 1.5rem;
	}

	.hero-assurances li {
		display: inline-flex;
		align-items: center;
		gap: 0.55rem;
		font-size: 0.98rem;
		color: var(--ink-soft);
		font-weight: 500;
	}

	.hero-assurance-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
	}

	.hero-assurance-check svg {
		width: 13px;
		height: 13px;
	}

	.hero-aside {
		position: relative;
	}

	.spotlight-card {
		position: relative;
		margin: 0;
		padding: clamp(1.75rem, 3vw, 2.5rem);
		border-radius: 24px;
		background: var(--card);
		border: 1px solid var(--border);
		box-shadow: 0 34px 64px -36px rgba(24, 45, 77, 0.5);
	}

	.spotlight-mark {
		font-family: "Fraunces", Georgia, serif;
		font-size: 4rem;
		line-height: 0.5;
		color: var(--blue);
		margin-bottom: 0.5rem;
	}

	.spotlight-quote {
		font-size: 1.18rem;
		line-height: 1.55;
		color: var(--ink);
		margin: 0 0 1.5rem;
		font-weight: 500;
	}

	.spotlight-author {
		display: flex;
		align-items: center;
		gap: 0.85rem;
	}

	.spotlight-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 46px;
		height: 46px;
		border-radius: 50%;
		background: var(--blue);
		color: #ffffff;
		font-size: 1.15rem;
		font-weight: 700;
		flex-shrink: 0;
	}

	.spotlight-name {
		display: block;
		font-size: 1.02rem;
		font-weight: 700;
		color: var(--ink);
	}

	.spotlight-role {
		display: block;
		font-size: 0.9rem;
		color: var(--ink-muted);
	}

	.hero-chip {
		position: absolute;
		padding: 0.7rem 1.1rem;
		border-radius: 14px;
		background: var(--card);
		border: 1px solid var(--border);
		font-size: 0.92rem;
		font-weight: 600;
		color: var(--blue-deep);
		box-shadow: 0 18px 34px -22px rgba(24, 45, 77, 0.55);
	}

	.hero-chip-one {
		top: -1.25rem;
		left: -1rem;
	}

	.hero-chip-two {
		bottom: -1.5rem;
		right: -0.75rem;
	}

	.offerings {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
		scroll-margin-top: 96px;
	}

	.offerings-inner {
		max-width: 1160px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.92rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.8rem;
	}

	.block-heading {
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2rem, 3.2vw, 3rem);
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--ink);
		margin: 0;
	}

	.offerings-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.75rem;
	}

	.offering-card {
		position: relative;
		padding: 2.5rem 2rem;
		border-radius: 22px;
		background: var(--page);
		border: 1px solid var(--border);
		transition: transform 0.3s ease, box-shadow 0.3s ease;
	}

	.offering-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 28px 54px -32px rgba(24, 45, 77, 0.45);
	}

	.offering-step {
		font-family: "Fraunces", Georgia, serif;
		display: block;
		font-size: 2.4rem;
		font-weight: 600;
		line-height: 1;
		color: var(--sky);
		margin-bottom: 0.75rem;
	}

	.offering-label {
		display: inline-block;
		font-size: 0.88rem;
		font-weight: 700;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.9rem;
	}

	.offering-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: 1.5rem;
		font-weight: 600;
		line-height: 1.2;
		color: var(--ink);
		margin: 0 0 0.9rem;
	}

	.offering-description {
		font-size: 1.06rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0;
	}

	.quality {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--page);
	}

	.quality-inner {
		max-width: 920px;
		margin: 0 auto;
	}

	.quality-card {
		padding: clamp(2rem, 4vw, 3rem);
		border-radius: 24px;
		background: var(--card);
		border: 1px solid var(--border);
		box-shadow: 0 34px 64px -36px rgba(24, 45, 77, 0.45);
	}

	.quality-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 1rem;
	}

	.quality-item {
		display: flex;
		align-items: flex-start;
		gap: 0.9rem;
		padding: 0.85rem 0;
		border-bottom: 1px solid var(--border);
	}

	.quality-item:last-child {
		border-bottom: none;
	}

	.quality-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
		margin-top: 0.1rem;
	}

	.quality-check svg {
		width: 15px;
		height: 15px;
	}

	.quality-text {
		font-size: clamp(1rem, 1.35vw, 1.12rem);
		line-height: 1.55;
		color: var(--ink);
		font-weight: 500;
	}

	.quality-summary {
		font-family: "Fraunces", Georgia, serif;
		font-style: italic;
		font-size: clamp(1.05rem, 1.4vw, 1.2rem);
		line-height: 1.5;
		color: var(--blue-deep);
		margin: 1.5rem 0 0;
		text-align: center;
	}

	.offer {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.offer-inner {
		max-width: 1160px;
		margin: 0 auto;
	}

	.offer-card {
		display: grid;
		grid-template-columns: 1.1fr 0.9fr;
		gap: clamp(2rem, 5vw, 4rem);
		align-items: center;
	}

	.offer-heading {
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2rem, 3.4vw, 3rem);
		font-weight: 600;
		letter-spacing: -0.02em;
		line-height: 1.1;
		color: var(--ink);
		margin: 0.5rem 0 1.25rem;
	}

	.offer-lead {
		font-size: clamp(1.05rem, 1.35vw, 1.2rem);
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.offer-list {
		list-style: none;
		margin: 0 0 2rem;
		padding: 0;
		display: grid;
		gap: 0.9rem;
	}

	.offer-item {
		display: flex;
		align-items: flex-start;
		gap: 0.85rem;
		font-size: 1.05rem;
		line-height: 1.5;
		color: var(--ink);
	}

	.offer-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
		margin-top: 0.1rem;
	}

	.offer-check svg {
		width: 15px;
		height: 15px;
	}

	.offer-note {
		margin: 1rem 0 0;
		font-size: 0.95rem;
		color: var(--ink-muted);
	}

	.deal-card {
		position: relative;
		padding: clamp(2rem, 4vw, 2.75rem);
		border-radius: 24px;
		background: linear-gradient(160deg, var(--blue-soft) 0%, var(--sky-light) 100%);
		border: 1px solid var(--border);
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(24, 45, 77, 0.45);
	}

	.deal-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 1rem;
	}

	.deal-headline {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
	}

	.deal-percent {
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(4.5rem, 11vw, 7rem);
		font-weight: 600;
		line-height: 0.85;
		letter-spacing: -0.04em;
		color: var(--blue-deep);
	}

	.deal-percent-label {
		font-size: clamp(1rem, 1.5vw, 1.2rem);
		font-weight: 600;
		line-height: 1.25;
		color: var(--ink);
		text-align: left;
	}

	.deal-applied {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		margin: 1.75rem 0 0;
		padding: 0.55rem 1rem;
		border-radius: 999px;
		background: var(--card);
		font-size: 0.92rem;
		font-weight: 600;
		color: var(--blue-deep);
	}

	.deal-applied-check {
		color: var(--blue);
		font-weight: 800;
	}

	.cta {
		padding: clamp(3.5rem, 6vw, 5.5rem) 1.5rem;
		background: var(--page);
	}

	.cta-inner {
		max-width: 1000px;
		margin: 0 auto;
	}

	.cta-card {
		position: relative;
		overflow: hidden;
		padding: clamp(3rem, 5vw, 4.5rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 28px;
		background: var(--cta);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(42, 84, 168, 0.75);
	}

	.cta-decoration {
		position: absolute;
		top: -50%;
		right: -10%;
		width: 420px;
		height: 420px;
		border-radius: 50%;
		background: radial-gradient(circle, rgba(255, 255, 255, 0.16), transparent 70%);
		pointer-events: none;
	}

	.cta-title {
		position: relative;
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2rem, 3.5vw, 3rem);
		font-weight: 600;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.5rem;
		max-width: 22ch;
		color: #ffffff;
	}

	.cta-lead {
		position: relative;
		font-size: clamp(1.1rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: rgba(255, 255, 255, 0.92);
		max-width: 58ch;
		margin: 0 auto 2.5rem;
	}

	.cta-actions {
		position: relative;
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 1.75rem;
	}

	.cta-offer {
		position: relative;
		font-size: 1.05rem;
		color: rgba(255, 255, 255, 0.92);
		margin: 0;
	}

	.cta-offer strong { color: #ffffff; }

	.testimonials {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
	}

	.testimonials-inner {
		max-width: 1240px;
		margin: 0 auto;
	}

	.testimonials-carousel {
		margin-top: 1rem;
	}

	.testimonial-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 2.25rem 2rem 2rem;
		border-radius: 22px;
		background: var(--page);
		border: 1px solid var(--border);
		margin: 0;
		box-sizing: border-box;
	}

	.testimonial-mark {
		font-family: "Fraunces", Georgia, serif;
		font-size: 3.5rem;
		line-height: 0.5;
		color: var(--blue);
		margin-bottom: 0.65rem;
	}

	.testimonial-quote {
		flex: 1;
		font-size: 1.05rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.testimonial-author {
		display: flex;
		align-items: center;
		gap: 0.7rem;
		font-size: 1rem;
		font-weight: 600;
		color: var(--ink);
	}

	.testimonial-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 38px;
		height: 38px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.95rem;
		font-weight: 700;
	}

	.site-footer {
		padding: 2.5rem 1.5rem;
		background: var(--page);
		border-top: 1px solid var(--border);
	}

	.site-footer-inner {
		max-width: 1180px;
		margin: 0 auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.footer-copy {
		font-size: 0.92rem;
		color: var(--ink-muted);
		margin: 0;
	}

	.modal-overlay {
		--page: #f4f8fd;
		--card: #ffffff;
		--ink: #182d4d;
		--ink-soft: #44546f;
		--ink-muted: #76859c;
		--blue: #3b6fd4;
		--blue-deep: #2a54a8;
		--blue-soft: #dce8fb;
		--border: #e2eaf6;
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(24, 45, 77, 0.5);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		cursor: pointer;
		z-index: 100000;
		pointer-events: auto;
	}

	.modal-dialog {
		position: relative;
		pointer-events: auto;
		width: 100%;
		max-width: 500px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		border-radius: 24px;
		background: var(--card);
		box-shadow: 0 40px 90px -30px rgba(24, 45, 77, 0.5);
		cursor: default;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.75rem) clamp(1.75rem, 3vw, 2.25rem);
	}

	.modal-close {
		position: absolute;
		top: 1.1rem;
		right: 1.25rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		line-height: 1;
		border-radius: 12px;
		border: none;
		background: var(--page);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
		z-index: 1;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-close svg {
		width: 18px;
		height: 18px;
	}

	.modal-badge {
		display: inline-block;
		box-sizing: border-box;
		max-width: calc(100% - 3rem);
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.04em;
		margin-bottom: 0.9rem;
	}

	.modal-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: 1.9rem;
		font-weight: 600;
		margin: 0 0 0.6rem;
		color: var(--ink);
	}

	.modal-description {
		font-size: 1.05rem;
		line-height: 1.55;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.modal-field {
		margin-bottom: 1.2rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 1rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.5rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		border: 2px solid var(--border);
		background: var(--page);
		font-family: inherit;
		font-size: 1.05rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea { resize: vertical; }

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: #ffffff;
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(59, 111, 212, 0.18);
	}

	.modal-field input:disabled,
	.modal-field textarea:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		-webkit-animation: btn-spin 0.7s linear infinite;
		animation: btn-spin 0.7s linear infinite;
	}

	@-webkit-keyframes btn-spin {
		to { -webkit-transform: rotate(360deg); }
	}

	@keyframes btn-spin {
		to { transform: rotate(360deg); }
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active { transition: opacity 0.25s ease; }

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog { transition: transform 0.25s ease, opacity 0.25s ease; }

	.modal-fade-enter-from,
	.modal-fade-leave-to { opacity: 0; }

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog { transform: translateY(14px) scale(0.98); opacity: 0; }

	@media (max-width: 900px) {
		.hero-inner { grid-template-columns: 1fr; }

		.hero-aside { margin-top: 1rem; }

		.hero-chip-one { left: 0; }

		.hero-chip-two { right: 0; }

		.offerings-grid { grid-template-columns: 1fr; }

		.offer-card { grid-template-columns: 1fr; }

		.deal-card { order: -1; }
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }

		.hero-chip { display: none; }

		.brand-logo { height: 40px; }

		.brand-logo-footer { height: 38px; }

		.offerings { scroll-margin-top: 72px; }

		.hero-assurances { gap: 0.65rem; }

		.site-footer-inner { justify-content: center; text-align: center; }
	}
</style>